import React, { memo, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, Tooltip } from '@Sage/hoi-poi-ui';
import { COMPANIES } from 'constants/Entities';
import EntityMenu from 'containers/components/EntityMenu';
import { canManageMarketingCampaigns } from 'containers/campaigns/marketing/utils/marketingCampaignPermissions';
import useMarketingBulkSubscribeExtraActions from 'containers/campaigns/marketing/bulkSubscribe/useMarketingBulkSubscribeExtraActions';
import { getIncludedSelectionIds } from 'utils/bulkSelection';
import { getLiteral } from 'utils/getLiteral';
import SearchCompanies from './components/SearchCompanies';

function mapStateToProps(state) {
    let canCreate =
        state.config.permission.crud_permission?.[COMPANIES.permission]?.create || false;
    let selection = state.entityListSelect[COMPANIES.entity];
    let companiesBulkActions = state.config.permission.companiesBulkActions;
    const canMergeAccounts = state.config.permission.merge_companies;
    let createEventBulk = state.config?.permission?.createEventBulk;
    const enableBulkOperations =
        createEventBulk || companiesBulkActions || state.config?.permission?.allowSendEmails;

    return {
        canCreate,
        selectionToken: selection && selection.token ? selection.token : null,
        selectedAccounts: selection && selection.total ? selection.total : 0,
        selectedIds: getIncludedSelectionIds(selection),
        selectedInfo: selection && selection.info ? selection.info : null,
        enableBulkOperations,
        canMergeAccounts,
        companiesBulkActions,
        showMarketingBulkSubscribe: enableBulkOperations && canManageMarketingCampaigns(state),
    };
}

const Menu = memo(
    ({
        enableBulkOperations,
        selectedAccounts,
        selectedIds,
        canMergeAccounts,
        canCreate,
        canOpenLeads,
        config,
        hideSearch,
        companiesBulkActions,
        onOpenLeads,
        onRequestBulkSubscribedConfirm,
        showMarketingBulkSubscribe,
    }) => {
        const bulkOperationsLiterals = useMemo(
            () => ({
                confirmInfoNumEdited: 'confirm_info_num_account_edited',
                confirmInfoNumDeleted: 'confirm_info_num_account_deleted',
                entityNameSingular: 'label_account_singular',
                entityNamePlural: 'label_accounts',
                confirmEditModalTitle: 'title_edit_accounts',
                confirmDeleteModalTitle: 'title_delete_accounts',
                labelTitleMenuTotalSelected: 'label_num_accounts_selected',
                labelTitleMenuTotalSelectedSingular: 'label_num_accounts_selected_singular',
                labelEditTextInfo: 'label_edit_fields_info',
                labelEditTextInfoSingular: 'label_edit_fields_info_singular',
                labelDeleteTextInfo: 'label_delete_fields_info',
                labelActionCantUndone: 'label_action_cant_undone',
                labelLoadingConfirmEditModalTitle: 'label_bulk_loading_edit',
                labelLoadingConfirmDeleteModalTitle: 'label_bulk_loading_delete',
                labelWriteXToConfirm: 'label_type_confirmation',
                labelWriteEdit: 'action_edit',
                labelWriteDelete: 'action_delete',
                labelSuccessEditSingular: 'success_edit_account',
                labelSuccessDeleteSingular: 'success_delete_account',
                labelSuccessEditPlural: 'success_bulk_edit_accounts',
                labelSuccessDeletePlural: 'success_bulk_delete_accounts',
            }),
            [],
        );

        const extraBulkActions = useMarketingBulkSubscribeExtraActions({
            entity: COMPANIES,
            filterKey: COMPANIES.entity,
            bulkSelectedEntities: selectedAccounts,
            selectedIds,
            onRequestBulkSubscribedConfirm,
            enabled: showMarketingBulkSubscribe,
        });

        return (
            <EntityMenu
                entity={COMPANIES}
                withFilters={true}
                withAutomationsButton
                canCreate={canCreate}
                canMerge={canMergeAccounts}
                canMassiveUpdate={companiesBulkActions}
                canMassiveDelete={companiesBulkActions}
                withBulkOperations={enableBulkOperations}
                bulkOperationsLiterals={bulkOperationsLiterals}
                bulkSelectedEntities={selectedAccounts}
                extraBulkActions={extraBulkActions}
                withViews={config.useViewsInMenu}
                withOptions={canOpenLeads}
                options={
                    canOpenLeads ? (
                        <Tooltip
                            placement="top"
                            content={getLiteral('tooltip_leads_map_prospecting')}
                        >
                            <Button
                                className="fm-companies-header__leads-cta"
                                type="secondary"
                                size="medium"
                                icon="accountsAdd"
                                onClick={onOpenLeads}
                                aria-label={getLiteral('tooltip_leads_map_prospecting')}
                            />
                        </Tooltip>
                    ) : null
                }
                quickFilters={config.quickFilters}
                useQuickView={config.useQuickView}
                searchComponent={!hideSearch ? <SearchCompanies /> : null}
                help={config.help}
            />
        );
    },
);

export default connect(mapStateToProps)(Menu);
